import { BRAND, COACH_PHOTO_PRIMARY, COACH_PHOTO_GYM } from "@/data/content";
import { Award, Phone, MessageSquare } from "lucide-react";

export default function Coach() {
  return (
    <section
      id="coach"
      data-testid="coach-section"
      className="relative bg-ng-bg py-24 md:py-36 border-t border-ng-line hex-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Photo column */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-ng-line">
              <img
                src={COACH_PHOTO_PRIMARY}
                alt={BRAND.founder_name}
                className="w-full h-full object-cover object-top"
                data-testid="coach-photo-primary"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ng-bg via-ng-bg/10 to-transparent" />
              <div className="absolute top-4 left-4 w-12 h-12 border-t-2 border-l-2 border-ng-blue" />
              <div className="absolute bottom-4 right-4 w-12 h-12 border-b-2 border-r-2 border-ng-blue" />
              {/* ISSA badge */}
              <div className="absolute top-4 right-4 bg-ng-bg/85 backdrop-blur-md border border-ng-blue/50 px-3 py-2 flex items-center gap-2">
                <Award size={14} strokeWidth={1.5} className="text-ng-blue" />
                <span className="font-display font-bold text-[10px] uppercase tracking-[0.22em] text-white">
                  ISSA Certified
                </span>
              </div>
            </div>

            {/* Secondary gym photo */}
            <div className="absolute -bottom-8 -right-8 hidden md:block w-44 aspect-[3/4] overflow-hidden border-2 border-ng-blue/60 shadow-[0_0_40px_-12px_rgba(30,144,255,0.6)]">
              <img
                src={COACH_PHOTO_GYM}
                alt="Coach J training"
                className="w-full h-full object-cover"
                data-testid="coach-photo-gym"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ng-bg/60 to-transparent" />
            </div>
          </div>

          {/* Copy column */}
          <div className="lg:col-span-7">
            <div className="label-eyebrow mb-5">04 · The Coach</div>
            <h2 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight text-white leading-[0.95]">
              Meet <span className="text-ng-blue italic">Coach J</span>.
            </h2>

            <p className="mt-4 font-display font-semibold uppercase tracking-[0.18em] text-white/80 text-sm md:text-base">
              Owner · Founder · {BRAND.cert_short}
            </p>

            <div className="mt-8 space-y-5 text-white/70 font-light leading-relaxed">
              <p>
                I&rsquo;m the owner and founder of NEXTGEN PERFORMANCE &mdash; an ISSA Certified Personal Trainer, Nutrition Coach, and Bodybuilding Specialist based in {BRAND.city}, serving Southern Oregon and online clients worldwide.
              </p>
              <p>
                NEXTGEN was built on one principle: real transformation requires the whole system &mdash; not just a workout plan. Training, nutrition, recovery, mindset, and (where appropriate) advanced optimization tools all matter.
              </p>
              <p className="text-white font-medium">
                Ready to put in the work? <span className="text-ng-blue">Text or call Coach J at {BRAND.phone}</span> &mdash; or apply below.
              </p>
            </div>

            {/* Certifications grid */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {BRAND.certifications.map((cert) => (
                <div
                  key={cert}
                  data-testid={`cert-${cert.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/g, "")}`}
                  className="border border-ng-line bg-ng-surface/60 p-4 flex items-center gap-3"
                >
                  <Award size={18} strokeWidth={1.5} className="text-ng-blue flex-shrink-0" />
                  <span className="font-display font-bold uppercase text-xs text-white tracking-wider leading-tight">
                    {cert}
                  </span>
                </div>
              ))}
            </div>

            {/* Contact buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${BRAND.phone_link}`}
                data-testid="coach-cta-call"
                className="inline-flex items-center justify-center gap-3 bg-ng-blue text-white px-6 py-3 uppercase tracking-[0.18em] text-xs font-bold font-display hover:bg-ng-blueLight transition-colors shadow-[0_0_30px_-8px_rgba(30,144,255,0.6)]"
              >
                <Phone size={14} strokeWidth={2} />
                Call Coach J
              </a>
              <a
                href={`sms:${BRAND.phone_link}`}
                data-testid="coach-cta-text"
                className="inline-flex items-center justify-center gap-3 border border-ng-line text-white px-6 py-3 uppercase tracking-[0.18em] text-xs font-bold font-display hover:border-ng-blue hover:text-ng-blue transition-colors"
              >
                <MessageSquare size={14} strokeWidth={2} />
                Text Coach J
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
